import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, ArrowsCounterClockwise, User, AddressBook } from "@phosphor-icons/react";

const UFS = ["AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG","PA","PB","PR","PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO"];

const buildCustomerPayload = (form) => ({
  customer: form.customer,
  customer_doc: form.customer_doc.replace(/\D/g, ""),
  customer_email: form.customer_email,
  customer_phone: form.customer_phone,
  customer_address: {
    address: form.address,
    number: form.number,
    complement: form.complement,
    neighborhood: form.neighborhood,
    city: form.city,
    state: form.state.toUpperCase(),
    zipcode: form.zipcode.replace(/\D/g, ""),
  },
});

const formFromOrder = (o) => {
  const a = o.customer_address || {};
  return {
    customer: o.customer || "",
    customer_doc: o.customer_doc || "",
    customer_email: o.customer_email || "",
    customer_phone: o.customer_phone || "",
    address: a.address || "",
    number: a.number || "",
    complement: a.complement || "",
    neighborhood: a.neighborhood || "",
    city: a.city || "",
    state: (a.state || "").toUpperCase(),
    zipcode: a.zipcode || "",
  };
};

/**
 * Modal para corrigir dados do cliente e/ou endereço antes de reprocessar a NF-e.
 * Fluxo:
 * 1. Carrega o pedido de origem (`order_id` do doc NFe)
 * 2. Mostra formulário pré-preenchido com nome/documento/e-mail/telefone + endereço
 * 3. Ao salvar: PATCH /orders/{id} com os novos dados + POST /fiscal/nfe/{id}/retry
 */
export default function NfeFixCustomerModal({ nfe, onClose, onRetried }) {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    customer: "", customer_doc: "", customer_email: "", customer_phone: "",
    address: "", number: "", complement: "", neighborhood: "", city: "", state: "", zipcode: "",
  });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/orders/${nfe.order_id}`);
        const o = data.item || data;
        setOrder(o);
        setForm(formFromOrder(o));
      } catch (e) {
        toast.error("Falha ao carregar pedido: " + (e?.response?.data?.detail || e.message));
      } finally {
        setLoading(false);
      }
    })();
  }, [nfe.order_id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const saveAndRetry = async () => {
    setSaving(true);
    try {
      // 1) Atualiza os campos no pedido (endpoint específico usado pelo módulo)
      await api.patch(`/orders/${nfe.order_id}/customer`, buildCustomerPayload(form));
      // 2) Dispara o retry
      await api.post(`/fiscal/nfe/${nfe.id}/retry`);
      toast.success("Dados atualizados. Reprocessando NF-e — acompanhe o status");
      onRetried && onRetried();
      onClose();
    } catch (e) {
      toast.error("Falha: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => !saving && onClose()}>
      <div className="bg-white border border-black w-full max-w-2xl max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="nfe-fix-customer-modal">
        <div className="border-b border-black px-5 py-4 flex items-start justify-between sticky top-0 bg-white">
          <div>
            <div className="overline text-zinc-500 text-xs">Rejeição em dados do cliente</div>
            <div className="font-head font-black text-lg flex items-center gap-2">
              <ArrowsCounterClockwise size={18} weight="bold" /> Corrigir e reprocessar NF-e
            </div>
            {nfe.rejection_reason && (
              <div className="mt-2 text-xs bg-red-50 border border-red-200 text-red-700 p-2">
                <b>Motivo da rejeição:</b> {nfe.rejection_reason}
              </div>
            )}
          </div>
          <button onClick={onClose} className="p-1 hover:bg-zinc-100" data-testid="nfe-fix-close"><X size={18} /></button>
        </div>

        {loading ? (
          <div className="p-8 text-center text-zinc-400">Carregando pedido…</div>
        ) : (
          <div className="p-5 space-y-5">
            <Section icon={User} title="Cliente">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Nome / Razão Social" span="full">
                  <input value={form.customer} onChange={(e) => set("customer", e.target.value)} className="input" data-testid="nfe-fix-customer" />
                </Field>
                <Field label="CPF / CNPJ">
                  <input value={form.customer_doc} onChange={(e) => set("customer_doc", e.target.value)} className="input font-mono" data-testid="nfe-fix-doc" />
                </Field>
                <Field label="E-mail">
                  <input value={form.customer_email} onChange={(e) => set("customer_email", e.target.value)} className="input" type="email" data-testid="nfe-fix-email" />
                </Field>
                <Field label="Telefone">
                  <input value={form.customer_phone} onChange={(e) => set("customer_phone", e.target.value)} className="input font-mono" data-testid="nfe-fix-phone" />
                </Field>
              </div>
            </Section>

            <Section icon={AddressBook} title="Endereço">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Field label="CEP">
                  <input value={form.zipcode} onChange={(e) => set("zipcode", e.target.value)} className="input font-mono" data-testid="nfe-fix-zipcode" />
                </Field>
                <Field label="Logradouro" span={2}>
                  <input value={form.address} onChange={(e) => set("address", e.target.value)} className="input" data-testid="nfe-fix-address" />
                </Field>
                <Field label="Número">
                  <input value={form.number} onChange={(e) => set("number", e.target.value)} className="input font-mono" data-testid="nfe-fix-number" />
                </Field>
                <Field label="Complemento" span={2}>
                  <input value={form.complement} onChange={(e) => set("complement", e.target.value)} className="input" data-testid="nfe-fix-complement" />
                </Field>
                <Field label="Bairro">
                  <input value={form.neighborhood} onChange={(e) => set("neighborhood", e.target.value)} className="input" data-testid="nfe-fix-neighborhood" />
                </Field>
                <Field label="Cidade">
                  <input value={form.city} onChange={(e) => set("city", e.target.value)} className="input" data-testid="nfe-fix-city" />
                </Field>
                <Field label="UF">
                  <select value={form.state} onChange={(e) => set("state", e.target.value)} className="input" data-testid="nfe-fix-state">
                    <option value="">—</option>
                    {UFS.map((u) => <option key={u} value={u}>{u}</option>)}
                  </select>
                </Field>
              </div>
            </Section>
          </div>
        )}

        <div className="border-t border-black px-5 py-3 flex justify-end gap-2 sticky bottom-0 bg-white">
          <button onClick={onClose} disabled={saving} className="px-4 py-2 text-sm border border-black hover:bg-zinc-100" data-testid="nfe-fix-cancel">Cancelar</button>
          <button onClick={saveAndRetry} disabled={saving || loading} className="px-4 py-2 text-sm bg-black text-white font-bold hover:bg-zinc-800 disabled:opacity-50 flex items-center gap-2" data-testid="nfe-fix-save-retry">
            <ArrowsCounterClockwise size={14} weight="bold" />
            {saving ? "Salvando e reprocessando…" : "Salvar e reprocessar"}
          </button>
        </div>
      </div>

      <style>{`
        .input { border: 1px solid #000; padding: 8px 12px; width: 100%; font-size: 14px; background: #fff; color: #000; }
      `}</style>
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <section>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={16} weight="bold" />
        <div className="font-bold text-sm uppercase tracking-wide">{title}</div>
      </div>
      {children}
    </section>
  );
}

function Field({ label, children, span }) {
  const cls = span === "full" ? "md:col-span-full" : span === 2 ? "md:col-span-2" : "";
  return (
    <div className={cls}>
      <div className="overline text-[10px] text-zinc-500 mb-1">{label}</div>
      {children}
    </div>
  );
}
